import { useEffect, useMemo, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCan, useMe } from "../../lib/auth";
import { useScrollLock } from "../../lib/hooks";
import { Icon, type IconName } from "../ui/Icon";
import { isAppShell } from "../../lib/platform";
import { ThemeToggle } from "../ui/ThemeToggle";

/**
 * THE header. One component, one markup, one mobile sheet — rendered by every
 * surface on the site: the landing page, the public content pages
 * (`/library`, `/calendar`, `/news`, a document, an event…), the dashboard and
 * the course learner pages.
 *
 * There used to be two — `landing/Nav` for public routes and `PlatformHeader`
 * for authenticated ones — and crossing between them was visible: opening a
 * document from `/dashboard/library` navigates to the PUBLIC `/library/:slug`
 * route, so the header swapped mid-journey and offered "Dashboard" to someone
 * already in it. Two headers cannot be kept identical by discipline; there is
 * now one, and the only thing that varies is WHAT IT CONTAINS:
 *
 *   signed out — the public tabs, plus Sign in / Begin
 *   signed in  — the RBAC tabs, plus Dashboard / Sign out
 *
 * Same element, same classes, same breakpoints, same sheet. Nothing about the
 * chrome moves when you sign in or cross between a public and an app route.
 */

interface Tab {
  to: string;
  label: string;
  icon: IconName;
  /** NavLink `end` — exact match (Home only). */
  end: boolean;
}

const EMPTY_TABS: ReadonlyArray<Tab> = [];

/** Signed OUT: the landing's own sections plus the two public content surfaces. */
const PUBLIC_TABS: ReadonlyArray<Tab> = [
  { to: "/#mission", label: "Mission", icon: "flower", end: false },
  { to: "/#vedas", label: "The Vedas", icon: "book", end: false },
  { to: "/#practice", label: "Practice", icon: "sound-high", end: false },
  { to: "/#house", label: "House of Unity", icon: "community", end: false },
  { to: "/library", label: "Library", icon: "book-stack", end: false },
  { to: "/calendar", label: "Calendar", icon: "calendar", end: false },
];

// Signed IN: tabs every member can use (browsing/consumption + their profile).
const BASE_TABS: ReadonlyArray<Tab> = [
  { to: "/dashboard", label: "Home", icon: "home", end: true },
  { to: "/courses", label: "Courses", icon: "graduation-cap", end: false },
  { to: "/dashboard/library", label: "Library", icon: "book-stack", end: false },
  { to: "/dashboard/calendar", label: "Calendar", icon: "calendar", end: false },
];
// Authoring/management tabs — each gated on the permission its destination needs.
// ONE door for authoring. Articles, Documents and YouTube were three separate
// top-level tabs, which put an author's tools in front of every member who
// could see them and crowded out the reading surfaces. They are one activity —
// editing what the site publishes — so they live behind `/dashboard/editor`,
// and the tab does not exist at all for someone who can edit nothing.
const EDITOR_TAB: Tab = { to: "/dashboard/editor", label: "Editor", icon: "settings", end: false };
const PROFILE_TAB: Tab = { to: "/dashboard/profile", label: "Profile", icon: "user", end: false };

/**
 * The tab list — the single source of truth for both the bar and the sheet.
 *
 * Every `useCan` runs unconditionally on both branches: they are hooks, and a
 * signed-out visitor must not change the hook order when their session
 * resolves. `useCan` is false for them anyway.
 */
export function useHeaderTabs(): ReadonlyArray<Tab> {
  const { data, isPending } = useMe();
  const signedIn = !!data?.user;
  // Auth not yet resolved and nothing cached to go on. Render NO tabs rather
  // than the signed-out set: showing "Mission / Sign in / Begin" for a moment
  // and then replacing it with the platform tabs is the flash of being logged
  // out that the app was reported for. A warm start never reaches this — the
  // persisted query cache already holds `me` — so it is a cold launch only.
  const unresolved = isPending && !data;   // see RequireAuth: isLoading lies during cache restore
  // Mirror each tab's server-side gate so the link never surfaces for a role
  // that would hit a 403 at the destination.
  const canAuthorArticles = useCan("articles.create");
  const canAuthorDocuments = useCan("documents.create");
  const canYoutube = useCan("youtube.manage");
  return useMemo(() => {
    if (unresolved) return EMPTY_TABS;
    // The packaged app and the installed PWA have no landing page — `/` is
    // rewritten to /dashboard at boot — so the public tabs would point at
    // sections that cannot be reached from inside the shell. The app IS the
    // platform; it always gets the platform tabs.
    if (!signedIn && !isAppShell) return PUBLIC_TABS;
    const tabs: Tab[] = [...BASE_TABS];
    if (canAuthorArticles || canAuthorDocuments || canYoutube) tabs.push(EDITOR_TAB);
    tabs.push(PROFILE_TAB);
    return tabs;
  }, [unresolved, signedIn, canAuthorArticles, canAuthorDocuments, canYoutube]);
}

const tabClasses = (isActive: boolean) =>
  `font-grotesk text-[0.78rem] tracking-[0.16em] uppercase transition-colors duration-300 ${
    isActive ? "text-[var(--color-violet)]" : "text-[var(--color-ink-mute)] hover:text-[var(--color-ink)]"
  }`;

/** A landing anchor (`/#mission`) has no "active" state to compute, and NavLink
 *  would mark every one of them active on `/`. Route tabs get NavLink. */
function TabLink({ tab, onNavigate, className, children }: {
  tab: Tab;
  onNavigate?: () => void;
  className?: string | ((a: { isActive: boolean }) => string);
  children: React.ReactNode;
}) {
  if (tab.to.includes("#")) {
    const cls = typeof className === "function" ? className({ isActive: false }) : className;
    return <Link to={tab.to} onClick={onNavigate} className={cls}>{children}</Link>;
  }
  return (
    <NavLink to={tab.to} end={tab.end} onClick={onNavigate} className={className}>
      {children}
    </NavLink>
  );
}

/** Routes that ARE the platform. Inside them the header's call to action must
 *  not be "Dashboard" — that is where you already are. */
function isPlatformRoute(pathname: string): boolean {
  return pathname === "/dashboard" || pathname.startsWith("/dashboard/") ||
    pathname === "/courses" || pathname.startsWith("/courses/");
}

export function SiteHeader() {
  const { data, isLoading } = useMe();
  const user = data?.user ?? null;
  // Same reason as the tabs: until we know, show neither state.
  const unresolved = isLoading && !data;
  const { pathname } = useLocation();
  /**
   * What the right-hand slot is FOR: getting from where you are to where you
   * would go next. That makes it contextual by nature —
   *
   *   signed out                     Sign in · Begin
   *   signed in, on a public page    Dashboard — the way in
   *   signed in, inside the platform your account only; the tabs already carry
   *                                  Home, and offering "Dashboard" to someone
   *                                  standing on /dashboard is the redundancy
   *                                  that was reported.
   *
   * The header itself does not change — same element, same classes. Only this
   * one button knows where it is.
   */
  const insidePlatform = isAppShell || isPlatformRoute(pathname);
  const tabs = useHeaderTabs();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useScrollLock(menuOpen);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /**
   * Publish the header's real height as `--app-header-h`.
   *
   * The header is FIXED, so it is out of flow and a full-height surface below
   * it has no way to know how much room it takes. Measuring it here — rather
   * than hard-coding a number that drifts the moment the padding changes — is
   * what lets the chant editor sit exactly under it instead of beneath it.
   */
  const headerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (el === null) return;
    const publish = () => {
      document.documentElement.style.setProperty(
        "--app-header-h", `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          // Transparent at rest is a LANDING affordance — it exists so the hero
          // reads through the header. Inside the platform there is no hero, and
          // a transparent bar floating over a toolbar is just a bar you cannot
          // read: it is opaque on every app surface, scrolled or not.
          backgroundColor: scrolled || insidePlatform
            ? "color-mix(in srgb, var(--color-vellum) 94%, transparent)"
            : "transparent",
          backdropFilter: scrolled && !insidePlatform ? "saturate(140%) blur(14px)" : "none",
          WebkitBackdropFilter: scrolled && !insidePlatform ? "saturate(140%) blur(14px)" : "none",
          borderBottom:
            scrolled || insidePlatform
              ? "1px solid var(--color-rule)"
              : "1px solid transparent",
        }}
      >
        <div className="container-edit flex items-center justify-between py-2.5 md:py-3 gap-4">
          <Link to="/" className="flex items-center gap-3 group" aria-label="Veda Union, home">
            <img
              src="/logo-mark.svg"
              alt=""
              className="h-8 w-8 md:h-9 md:w-9 transition-transform duration-500 group-hover:rotate-[6deg]"
              loading="eager"
            />
            <span className="font-display text-[1.2rem] md:text-[1.35rem] tracking-tight leading-none text-[var(--color-ink)]">
              Veda <span className="font-display-italic">Union</span>
            </span>
          </Link>

          {/* `lg`, not `md`: six uppercase tabs plus the wordmark and the
              sign-in cluster do not fit across a 768–1023px tablet, and the row
              wrapped into three broken lines. Below `lg` the sheet handles it. */}
          <nav className="hidden lg:flex items-center gap-5 lg:gap-6" aria-label="Primary">
            {tabs.map((t) => (
              <TabLink key={t.to} tab={t} className={({ isActive }) => tabClasses(isActive)}>
                <span className="inline-flex items-center gap-1.5">
                  <Icon name={t.icon} size="1.05em" /> {t.label}
                </span>
              </TabLink>
            ))}
          </nav>

          {/* On a phone the bar is the logo and the hamburger, and NOTHING else
              — the theme toggle and the sign-in/dashboard actions live in the
              sheet, which is where they were before this header was unified.
              Leaving them in the bar crowded the top of every page. */}
          <div className="hidden lg:flex items-center gap-4 lg:gap-5">
            {user && (
              <span className="hidden xl:inline font-grotesk text-[0.74rem] tracking-[0.06em] text-[var(--color-ink-mute)]">
                {user.displayName}
              </span>
            )}
            <ThemeToggle />
            {unresolved ? null : user ? (
              <>
                {!insidePlatform && (
                  <Link to="/dashboard" className="btn-primary">
                    <span>Dashboard</span>
                    <Icon name="arrow-right" className="btn-arrow-icon" />
                  </Link>
                )}
                <Link
                  to="/logout"
                  className="font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] hover:text-[var(--color-violet)] transition-colors"
                >
                  Sign out
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="font-grotesk text-[0.78rem] tracking-[0.16em] uppercase text-[var(--color-ink)] hover:text-[var(--color-violet)] transition-colors duration-300"
                >
                  Sign in
                </Link>
                {/* Quiet, not filled. `Dashboard` is the one coloured call to
                    action in this header; `Begin` sits beside `Sign in`. */}
                <Link to="/register" className="btn-ghost">
                  <span>Begin</span>
                  <Icon name="arrow-right" className="btn-arrow-icon" />
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="lg:hidden p-2 -mr-2 text-[var(--color-ink)]"
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            <Icon name="menu" size="1.5rem" />
          </button>
        </div>
      </header>

      {/* Mobile sheet — the same tabs and the same actions as the bar above. */}
      <div className="mobile-sheet lg:hidden" data-open={menuOpen} aria-hidden={!menuOpen}>
        <div className="container-edit flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3" onClick={() => setMenuOpen(false)}>
            <img src="/logo-mark.svg" alt="" className="h-8 w-8" />
            <span className="font-display text-[1.45rem] tracking-tight leading-none">
              Veda <span className="font-display-italic">Union</span>
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="p-2 -mr-2 font-grotesk text-[0.78rem] tracking-[0.16em] uppercase"
            aria-label="Close menu"
          >
            Close
          </button>
        </div>
        <hr className="rule mx-6" />
        <nav className="container-edit pt-10 pb-12 flex flex-col gap-1" aria-label="Primary mobile">
          {tabs.map((t) => (
            <TabLink
              key={t.to}
              tab={t}
              onNavigate={() => setMenuOpen(false)}
              className="font-grotesk font-medium text-[1.4rem] leading-[1.2] tracking-tight py-3 border-b border-[var(--color-rule)] flex items-center justify-between"
            >
              <span className="inline-flex items-center gap-3">
                <Icon name={t.icon} size="1.4rem" className="text-[var(--color-violet)]" /> {t.label}
              </span>
              <Icon name="nav-right" size="1.1rem" className="text-[var(--color-ink-light)]" />
            </TabLink>
          ))}
          <div className="mt-8 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <span className="font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
                Theme
              </span>
              <ThemeToggle variant="segmented" />
            </div>
            {unresolved ? null : user ? (
              <>
                <p className="font-grotesk text-[0.78rem] tracking-[0.04em] text-[var(--color-ink-mute)]">
                  Signed in as {user.displayName}
                </p>
                {!insidePlatform && (
                  <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="btn-primary self-start">
                    <span>Dashboard</span>
                    <Icon name="arrow-right" className="btn-arrow-icon" />
                  </Link>
                )}
                <Link to="/logout" onClick={() => setMenuOpen(false)} className="btn-ghost">
                  <span>Sign out</span>
                  <Icon name="arrow-right" className="btn-arrow-icon" />
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="btn-ghost">
                  <span>Sign in</span>
                  <Icon name="arrow-right" className="btn-arrow-icon" />
                </Link>
                <Link to="/register" onClick={() => setMenuOpen(false)} className="btn-ghost self-start">
                  <span>Begin</span>
                  <Icon name="arrow-right" className="btn-arrow-icon" />
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </>
  );
}

export function PlatformFooter() {
  return (
    <footer className="container-edit py-7 border-t border-[var(--color-rule)] flex flex-col sm:flex-row items-center justify-between gap-3 font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
      <p>&copy; {new Date().getFullYear()} Veda Union</p>
      <Link to="/" className="inline-flex items-center gap-2 hover:text-[var(--color-violet)] transition-colors">
        <Icon name="nav-left" size="1em" /> Back to vedaunion.org
      </Link>
    </footer>
  );
}
